import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { usePostHog } from 'posthog-js/react'
import { toast } from 'sonner'
import { Loader2Icon, SparklesIcon } from 'lucide-react'
import type { DayOfWeek } from '@backend/lib/db/schema/automation'
import { trpc } from '@/config/trpc'
import { GradientAnimation } from '@/components/ui/GradientAnimation'
import { Button } from '@/components/ui/shadcn/button'
import { CompanySetupSchedulePicker } from '@/components/company-setup/company-setup-schedule-picker'
import type { DraftSchedule } from '@/stores/companySetup/companySetupStore'
import { useAnalyzeStream } from '@/pages/moodboard-page/use-analyze-stream'
import { useOnboardingStep } from '@/lib/onboarding/tracking'
import { captureError } from '@/lib/errors'

// Final leg of onboarding. Reached from the moodboard canvas via "Finalize &
// continue". Owns the analyze stream — kicked on mount, it generates the brief
// in the background (server-side, for the request's lifetime) while the user
// sets reminders. On finish we persist those, mark the brand onboarded, and land
// in the app without waiting for the brief: the stream keeps running across the
// in-app navigation (useAnalyzeStream only aborts when told to), and home holds
// generation until the board is ready. Language was chosen at
// the start of onboarding (so the brief generates in it). See
// docs/design/onboarding-moodboard.md.
type Step = 'reminders' | 'finishing'

export function CompanySetupFinishPage() {
  const navigate = useNavigate()
  const posthog = usePostHog()
  const [searchParams] = useSearchParams()
  const moodboardId = Number(searchParams.get('moodboard'))
  const validMoodboard = !Number.isNaN(moodboardId) && moodboardId > 0

  const { state: analyzeState, start: startAnalyze } = useAnalyzeStream(moodboardId)
  const finishStep = useOnboardingStep('finish', validMoodboard)
  // Kick the brief generation once on mount. It runs while the user fills the
  // step below; `force: false` short-circuits to the cache if it somehow ran
  // already. A brand-new onboarding board has no brief, so it runs fully.
  const startedRef = useRef(false)
  useEffect(() => {
    if (!validMoodboard || startedRef.current) return
    startedRef.current = true
    void startAnalyze(false)
  }, [validMoodboard, startAnalyze])

  const briefSettled = analyzeState.phase === 'done' || analyzeState.phase === 'error'

  const [step, setStep] = useState<Step>('reminders')
  const [draftSchedule, setDraftSchedule] = useState<DraftSchedule>({})
  const [sendReminders, setSendReminders] = useState(true)

  // Reminders are automations, and a plan with an automation cap of 0 (free)
  // cannot save any: automation.create rejects every row. Showing the step
  // anyway let users pick days that were silently dropped, so it only appears
  // when the plan can keep them. An unknown plan (query error) skips it too;
  // reminders can always be set up later from Automations.
  const { data: usage, isError: usageError } = trpc.usageLimit.get.useQuery()
  const canSaveReminders = usage ? usage.automations.max !== 0 : usageError ? false : undefined

  const { mutateAsync: createAutomation } = trpc.automation.create.useMutation()
  const { mutateAsync: markOnboarded } = trpc.brand.markOnboarded.useMutation()
  const utils = trpc.useUtils()

  const scheduleSlots = useMemo(
    () =>
      Object.entries(draftSchedule)
        .map(([day, platforms]) => ({ day: day as DayOfWeek, platforms: platforms ?? [] }))
        .filter((s) => s.platforms.length > 0),
    [draftSchedule],
  )

  // Persist reminders and finish. Reminder rows are best-effort (a failure
  // shouldn't trap the user in onboarding); marking onboarded is the gate, so it
  // always runs last. The brief may still be streaming; home waits for it.
  const [finishFailed, setFinishFailed] = useState(false)
  // `skipReminders` is explicit because clearing draftSchedule and finishing in
  // the same tick would still read the old schedule from this render.
  const finish = (skipReminders = false) => {
    setStep('finishing')
    setFinishFailed(false)
    void completeOnboarding(skipReminders)
  }

  const completeOnboarding = async (skipReminders: boolean) => {
    const slots = skipReminders ? [] : scheduleSlots
    // Moodboard-linked reminder automations.
    if (slots.length > 0) {
      const results = await Promise.allSettled(
        slots.map((slot) =>
          createAutomation({
            moodboardId,
            dayOfWeek: slot.day,
            platforms: slot.platforms,
            sendReminders,
          }),
        ),
      )
      const failed = results.filter((r) => r.status === 'rejected').length
      if (failed > 0) {
        console.warn(`Failed to create ${failed}/${slots.length} onboarding automations`)
        finishStep.track('onboarding_reminders_failed', { failed, total: slots.length })
      }
    }

    try {
      const updated = await markOnboarded()
      // Prime the cache synchronously so the Layout onboarding gate sees the
      // brand as onboarded before we navigate — avoids a flash back to
      // /company-setup while a refetch is in flight.
      utils.brand.getCurrent.setData(undefined, updated)
      void utils.brand.list.invalidate()
    } catch (error) {
      captureError(error, { area: 'onboarding', action: 'mark_onboarded' })
      finishStep.track('onboarding_finish_failed', {
        message: error instanceof Error ? error.message : String(error),
      })
      toast.error('Almost there. Something went wrong finishing setup. Please try again.')
      setFinishFailed(true)
      setStep('reminders')
      return
    }

    posthog.capture('onboarding_completed', {
      scheduled_automations: slots.length,
      send_reminders: sendReminders,
      brief_status: analyzeState.phase,
    })
    // The brief usually is still streaming here (it finishes on home), so
    // brief_phase is where it was, not how it ended. For the outcome, see the
    // backend's moodboard_analyzed / moodboard_analyze_failed.
    finishStep.complete({
      scheduled_automations: slots.length,
      skipped_reminders: skipReminders,
      reminders_offered: canSaveReminders === true,
      brief_phase: analyzeState.phase,
    })

    navigate('/')
  }

  // Nothing to ask: finish straight away, the user lands on home (which waits
  // for the brief itself).
  const autoFinishedRef = useRef(false)
  useEffect(() => {
    if (canSaveReminders !== false || !validMoodboard || autoFinishedRef.current) return
    autoFinishedRef.current = true
    void completeOnboarding(true)
  }, [canSaveReminders, validMoodboard]) // eslint-disable-line react-hooks/exhaustive-deps -- completeOnboarding is recreated every render; the ref makes this run once

  if (!validMoodboard) {
    return (
      <section className="relative min-h-screen">
        <GradientAnimation animationDuration="25s" />
        <div className="mx-auto flex min-h-screen max-w-[560px] flex-col items-center justify-center p-6 text-center">
          <p className="text-foreground text-lg font-semibold">We lost track of your moodboard.</p>
          <Button className="mt-4" onClick={() => navigate('/moodboards')}>
            Back to moodboards
          </Button>
        </div>
      </section>
    )
  }

  return (
    <section className="relative min-h-screen">
      <GradientAnimation animationDuration="25s" />
      <div className="mx-auto flex min-h-screen max-w-[640px] flex-col justify-center p-4 lg:p-8">
        <div className="bg-card/80 border-border w-full rounded-2xl border p-6 shadow-lg backdrop-blur lg:p-8">
          {/* Brief-progress hint, present across steps. */}
          <div className="text-muted-foreground mb-6 flex items-center gap-2 text-sm">
            {briefSettled ? (
              <>
                <SparklesIcon size={14} className="text-primary" />
                {analyzeState.phase === 'error'
                  ? 'Your brief will finish later. You can re-run it from your moodboard.'
                  : 'Your brand brief is ready.'}
              </>
            ) : (
              <>
                <Loader2Icon size={14} className="animate-spin" />
                Building your brand brief in the background…
              </>
            )}
          </div>

          {step === 'reminders' && canSaveReminders === true && (
            <div>
              <h1 className="text-foreground text-2xl font-bold">Stay consistent</h1>
              <p className="text-muted-foreground mt-1 text-sm">
                Pick the days you'd like CASSI to nudge you with a ready-to-edit post. You can
                change this anytime.
              </p>
              <div className="mt-6">
                <CompanySetupSchedulePicker
                  schedule={draftSchedule}
                  onScheduleChange={setDraftSchedule}
                  sendReminders={sendReminders}
                  onSendRemindersChange={setSendReminders}
                />
              </div>
              <div className="mt-8 flex items-center justify-between">
                <button
                  onClick={() => finish(true)}
                  className="text-muted-foreground hover:text-foreground text-sm underline underline-offset-2">
                  Skip for now
                </button>
                <Button onClick={() => finish()}>Finish</Button>
              </div>
            </div>
          )}

          {finishFailed && canSaveReminders === false && (
            <div className="flex flex-col items-center py-10 text-center">
              <p className="text-foreground text-base font-medium">
                We couldn't finish setting things up.
              </p>
              <Button className="mt-4" onClick={() => finish()}>
                Try again
              </Button>
            </div>
          )}

          {(step === 'finishing' || canSaveReminders !== true) &&
            !(finishFailed && canSaveReminders === false) && (
              <div className="flex flex-col items-center py-10 text-center">
                <Loader2Icon size={28} className="text-primary animate-spin" />
                <p className="text-foreground mt-4 text-base font-medium">Setting things up…</p>
                <p className="text-muted-foreground mt-1 text-sm">This only takes a moment.</p>
              </div>
            )}
        </div>
      </div>
    </section>
  )
}
